<template>
  <div class="seed-generator">
    <!-- Selection Mode -->
    <div v-if="!showWizard" class="mode-selection">
      <div class="welcome-section">
        <div class="icon-header">
          <i class="fas fa-seedling seed-icon"></i>
          <h3>Psychometric Data Generator</h3>
        </div>
        <p class="welcome-text">
          Create realistic test datasets with valid psychometric metrics.
          Choose a mode to get started:
        </p>
      </div>

      <!-- Quick Presets -->
      <div class="quick-presets">
        <h4><i class="fas fa-zap"></i> Quick Start</h4>
        <div class="preset-cards">
          <div 
            v-for="(preset, key) in presets" 
            :key="key"
            class="preset-card"
            @click="selectPreset(key, preset)"
          >
            <div class="preset-icon">{{ preset.icon }}</div>
            <h5>{{ preset.name }}</h5>
            <p>{{ preset.description }}</p>
            <div class="preset-metrics">
              <span>α ≥ {{ preset.targetAlpha }}</span>
              <span>{{ Math.round((preset.goodItemsRatio || 0.8) * 100) }}% items OK</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Expert Mode -->
      <div class="expert-mode">
        <h4><i class="fas fa-cogs"></i> Expert Mode</h4>
        <div class="expert-card" @click="showWizard = true">
          <div class="expert-content">
            <i class="fas fa-sliders-h expert-icon"></i>
            <div>
              <h5>Complete Configuration</h5>
              <p>Full control over all psychometric parameters</p>
            </div>
          </div>
          <i class="fas fa-arrow-right arrow-icon"></i>
        </div>
      </div>

      <!-- Parameter Guide -->
      <div class="parameter-guide">
        <h4><i class="fas fa-compass"></i> Parameter Guide</h4>
        <p class="guide-intro">Configure your parameters according to desired quality:</p>
        
        <div class="grade-cards">
          <div class="grade-card grade-a">
            <div class="grade-header">
              <span class="grade-badge grade-a-badge">A</span>
              <h5>Excellence</h5>
            </div>
            <div class="parameters">
              <div class="param">α ≥ 0.85</div>
              <div class="param">Disc. ≥ 0.4</div>
              <div class="param">Bruit ≤ 0.2</div>
            </div>
            <p class="description">Very high quality data for publications and research</p>
          </div>

          <div class="grade-card grade-b">
            <div class="grade-header">
              <span class="grade-badge grade-b-badge">B</span>
              <h5>Very Good</h5>
            </div>
            <div class="parameters">
              <div class="param">α ≥ 0.8</div>
              <div class="param">Disc. ≥ 0.3</div>
              <div class="param">Bruit ≤ 0.3</div>
            </div>
            <p class="description">Recommended quality for most uses</p>
          </div>

          <div class="grade-card grade-c">
            <div class="grade-header">
              <span class="grade-badge grade-c-badge">C</span>
              <h5>Acceptable</h5>
            </div>
            <div class="parameters">
              <div class="param">α ≥ 0.7</div>
              <div class="param">Disc. ≥ 0.2</div>
              <div class="param">Bruit ≤ 0.4</div>
            </div>
            <p class="description">Sufficient quality for exploratory testing</p>
          </div>

          <div class="grade-card grade-d">
            <div class="grade-header">
              <span class="grade-badge grade-d-badge">D</span>
              <h5>Training</h5>
            </div>
            <div class="parameters">
              <div class="param">α ≥ 0.6</div>
              <div class="param">Disc. ≥ 0.15</div>
              <div class="param">Bruit ≤ 0.5</div>
            </div>
            <p class="description">Training data and educational demonstration</p>
          </div>
        </div>

        <div class="guide-note">
          <i class="fas fa-info-circle"></i>
          <span>The final grade is determined by psychometric analysis in Test Manager.</span>
        </div>
      </div>

      <!-- History -->
      <div class="generation-history" v-if="history.length > 0">
        <h4><i class="fas fa-history"></i> Recent Generations</h4>
        <div class="history-list">
          <div 
            v-for="entry in history.slice(0, 3)" 
            :key="entry.id"
            class="history-item"
            @click="viewHistoryDetails(entry)"
          >
            <div class="history-status">
              <i :class="entry.success ? 'fas fa-check-circle text-success' : 'fas fa-times-circle text-danger'"></i>
            </div>
            <div class="history-content">
              <div class="history-title">{{ entry.name }}</div>
              <div class="history-meta">
                {{ formatDate(entry.date) }} • {{ entry.students }} students • Grade {{ entry.grade }}
              </div>
            </div>
            <div class="history-metrics">
              <span class="alpha-badge">α {{ entry.alpha }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Configuration Wizard -->
    <SeedWizard 
      v-if="showWizard"
      :preset-config="selectedPresetConfig"
      @close="showWizard = false"
      @generated="onGenerationComplete"
    />

    <!-- Progress Modal -->
    <GenerationProgress 
      v-if="showProgress"
      :progress="progressData"
      @close="showProgress = false"
    />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import SeedWizard from './SeedWizard.vue';
import GenerationProgress from './GenerationProgress.vue';
import api from '../../../services/axios.js';

// Simple notification system
const showNotification = (message, type = 'info') => {
  console.log(`[${type.toUpperCase()}] ${message}`);
  // Using native alerts for now
  if (type === 'error') {
    alert(`Error: ${message}`);
  } else if (type === 'success') {
    alert(`Success: ${message}`);
  }
};

// Reactive state
const showWizard = ref(false);
const showProgress = ref(false);
const selectedPresetConfig = ref(null);
const progressData = ref({});
const presets = ref({});
const history = ref([]);

// Methods
const selectPreset = async (key, preset) => {
  try {
    selectedPresetConfig.value = {
      ...preset,
      presetKey: key,
      quickStart: true
    };
    
    showNotification(`Configuration "${preset.name}" selected`, 'info');
    showWizard.value = true;
    
  } catch (error) {
    console.error('Preset selection error:', error);
    showNotification('Error during preset selection', 'error');
  }
};

const onGenerationComplete = (result) => {
  showWizard.value = false;
  
  // Add to history
  const historyEntry = {
    id: Date.now(),
    name: result.presetName || 'Custom configuration',
    date: new Date(),
    success: result.success,
    students: result.generated?.students || 0,
    grade: result.metrics?.qualityGrade || 'N/A',
    alpha: result.metrics?.cronbachAlpha?.toFixed(3) || '0.000',
    result: result
  };
  
  history.value.unshift(historyEntry);
  saveHistory();
  
  if (result.success) {
    showNotification(`✅ Generation successful! ${result.generated?.students || 0} students processed. Check psychometric analysis for final grade.`, 'success');
  } else {
    showNotification('Generation failed', 'error');
  }
};

const viewHistoryDetails = (entry) => {
  // Display details of a previous generation
  showNotification('Generation details (feature coming soon)', 'info');
};

const formatDate = (date) => {
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(date));
};

const loadPresets = async () => {
  try {
    console.log('🔍 Loading presets...');
    console.log('🔍 Token present:', !!localStorage.getItem('editortoken'));
    
    const response = await api.get('/seed/presets');
    console.log('✅ Presets loaded:', response.data);
    
    presets.value = response.data.presets;
  } catch (error) {
    console.error('❌ Preset loading error:', error);
    console.error('❌ Status:', error.response?.status);
    console.error('❌ Message:', error.response?.data);
    
    // Fallback with default presets
    presets.value = {
      'demo-realiste': {
        name: 'Realistic Demo',
        description: 'High quality data for demonstrations',
        targetAlpha: 0.85,
        minDiscrimination: 0.4,
        icon: '🎯'
      },
      'test-problematique': {
        name: 'Detection Test',
        description: 'Data with issues to test detection',
        targetAlpha: 0.4,
        icon: '🔍'
      },
      'formation-educative': {
        name: 'Educational Training',
        description: 'Educational data for learning',
        targetAlpha: 0.75,
        icon: '📚'
      }
    };
  }
};

const loadHistory = () => {
  try {
    const stored = localStorage.getItem('seedGenerator_history');
    if (stored) {
      history.value = JSON.parse(stored);
    }
  } catch (error) {
    console.error('History loading error:', error);
  }
};

const saveHistory = () => {
  try {
    // Keep only the last 10 generations
    const toSave = history.value.slice(0, 10);
    localStorage.setItem('seedGenerator_history', JSON.stringify(toSave));
  } catch (error) {
    console.error('History saving error:', error);
  }
};

// Lifecycle
onMounted(() => {
  loadPresets();
  loadHistory();
});
</script>

<style scoped>
.seed-generator {
  padding: 30px;
}

.welcome-section {
  text-align: center;
  margin-bottom: 40px;
}

.icon-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  margin-bottom: 15px;
}

.seed-icon {
  font-size: 2.5rem;
  color: #28a745;
}

.welcome-section h3 {
  color: #2c3e50;
  margin: 0;
  font-size: 1.8rem;
}

.welcome-text {
  color: #6c757d;
  font-size: 1.1rem;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
}

.quick-presets {
  margin-bottom: 40px;
}

.quick-presets h4,
.expert-mode h4,
.generation-history h4 {
  color: #495057;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 2px solid #e9ecef;
  font-size: 1.2rem;
}

.preset-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.preset-card {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 2px solid #dee2e6;
  border-radius: 15px;
  padding: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  position: relative;
  overflow: hidden;
}

.preset-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  border-color: #007bff;
}

.preset-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.preset-icon {
  font-size: 2rem;
  margin-bottom: 15px;
}

.preset-card h5 {
  color: #2c3e50;
  margin-bottom: 10px;
  font-size: 1.1rem;
}

.preset-card p {
  color: #6c757d;
  font-size: 0.9rem;
  margin-bottom: 15px;
  line-height: 1.4;
}

.preset-metrics {
  display: flex;
  justify-content: center;
  gap: 15px;
}

.preset-metrics span {
  background: #007bff;
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
}

.expert-mode {
  margin-bottom: 40px;
}

.expert-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 15px;
  padding: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.expert-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 15px 35px rgba(102, 126, 234, 0.3);
}

.expert-content {
  display: flex;
  align-items: center;
  gap: 20px;
}

.expert-icon {
  font-size: 2rem;
  opacity: 0.9;
}

.expert-card h5 {
  margin: 0 0 5px 0;
  font-size: 1.2rem;
}

.expert-card p {
  margin: 0;
  opacity: 0.9;
  font-size: 0.95rem;
}

.arrow-icon {
  font-size: 1.5rem;
  opacity: 0.8;
}

.generation-history {
  background: #f8f9fa;
  border-radius: 15px;
  padding: 25px;
}

.history-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.history-item {
  background: white;
  border-radius: 10px;
  padding: 15px;
  display: flex;
  align-items: center;
  gap: 15px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid #e9ecef;
}

.history-item:hover {
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.history-status i {
  font-size: 1.2rem;
}

.history-content {
  flex: 1;
}

.history-title {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 4px;
}

.history-meta {
  font-size: 0.85rem;
  color: #6c757d;
}

.alpha-badge {
  background: #28a745;
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
}

/* Parameter Guide */
.parameter-guide {
  margin-bottom: 40px;
  background: #f8f9fa;
  border-radius: 15px;
  padding: 25px;
}

.parameter-guide h4 {
  color: #495057;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 2px solid #e9ecef;
  font-size: 1.2rem;
}

.guide-intro {
  color: #6c757d;
  margin-bottom: 25px;
  font-size: 1rem;
  text-align: center;
}

.grade-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}

.grade-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  border: 2px solid transparent;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.grade-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
}

.grade-a::before { background: linear-gradient(135deg, #28a745 0%, #20c997 100%); }
.grade-b::before { background: linear-gradient(135deg, #17a2b8 0%, #007bff 100%); }
.grade-c::before { background: linear-gradient(135deg, #ffc107 0%, #fd7e14 100%); }
.grade-d::before { background: linear-gradient(135deg, #dc3545 0%, #6f42c1 100%); }

.grade-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.grade-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
}

.grade-badge {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: white;
  font-size: 0.9rem;
}

.grade-a-badge { background: #28a745; }
.grade-b-badge { background: #17a2b8; }
.grade-c-badge { background: #ffc107; }
.grade-d-badge { background: #dc3545; }

.grade-header h5 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.1rem;
}

.parameters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 15px;
}

.param {
  background: #e9ecef;
  color: #495057;
  padding: 4px 8px;
  border-radius: 10px;
  font-size: 0.8rem;
  font-weight: 500;
}

.description {
  color: #6c757d;
  font-size: 0.9rem;
  line-height: 1.4;
  margin: 0;
}

.guide-note {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 8px;
  padding: 12px 15px;
  color: #856404;
  font-size: 0.9rem;
}

.guide-note i {
  color: #f39c12;
}

/* Utility classes */
.text-success {
  color: #28a745 !important;
}

.text-danger {
  color: #dc3545 !important;
}

/* Responsive */
@media (max-width: 768px) {
  .seed-generator {
    padding: 20px;
  }
  
  .preset-cards {
    grid-template-columns: 1fr;
  }
  
  .expert-content {
    flex-direction: column;
    text-align: center;
    gap: 10px;
  }
  
  .history-item {
    flex-direction: column;
    text-align: center;
  }
}
</style>